import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ButtonWrap from "../../components/template/ButtonWrap"
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons"
import { useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState } from "react"
import Table, { SelectColumnFilter } from "../../components/table/Table"
import Modal from "../../components/template/Modal"
import ExperimentNewModal from "./ExperimentNewModal"
import MainTitle from "../../components/template/MainTitle"
import ButtonFull from "../../components/template/ButtonFull"
import api from "../../components/utils/api"
import ExperimentStatusPill from "./ExperimentStatusPill"
import PageDescription from "../../components/template/PageDescription"


const ExperimentsPage = () => {
    const [modalVisible, setModalVisible] = useState(false);
    const [data, setData] = useState([])
    // shown after a new experiment is queued; its record is created asynchronously
    // by the preaction worker, so it may not be in the list yet
    const [createdNoticeVisible, setCreatedNoticeVisible] = useState(false)
    // countdown before the Refresh button appears, giving the worker time to
    // insert the record so the first refresh actually shows the new row
    const [refreshCountdown, setRefreshCountdown] = useState(0)
    const openModal = () => { setModalVisible(true); }
    const closeModal = () => { setModalVisible(false); }

    const navigate = useNavigate()

    const fetchExperiments = useCallback(async () => {
        try {
            const res = await api.get(`/api/experiments?summary=true`)
            setData(res.data.experiments)
        } catch (error) {
            console.error(error)
        }
    }, [])

    useEffect(() => {
        fetchExperiments()
    }, [fetchExperiments])

    useEffect(() => {
        if (!createdNoticeVisible || refreshCountdown <= 0) return
        const timer = setTimeout(() => setRefreshCountdown(prev => prev - 1), 1000)
        return () => clearTimeout(timer)
    }, [createdNoticeVisible, refreshCountdown])

    const columns = useMemo(
        () => [
            {
                Header: "Created At",
                accessor: "created_at",
            },
            {
                Header: "Database",
                accessor: "database_summary.name",
                Filter: SelectColumnFilter,
                filter: 'exactText',
            },
            {
                Header: "Experiment",
                accessor: "version",
            },
            {
                Header: "Status",
                accessor: "status.name",
                Filter: SelectColumnFilter,
                filter: 'includes',
                Cell: ExperimentStatusPill,
            },
            {
                Header: "Detail",
                accessor: "detail",
                Cell: ({ row }) => <div>
                    <ButtonFull contents={"Detail"} onClick={() => navigate(`/experiments/${row.original.id}`)} />
                </div>
            },
        ], [navigate]
    )

    return (
        <div className='default-background'>
            <div className="flex justify-between items-center my-5">
                <MainTitle title="Experiments" />
                <ButtonWrap contents={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => navigate('/')} />
            </div>
            <div className="mb-4">
                <PageDescription contents={[
                    "On this page, you can create experiments — a data setup (class selection, sample limits) that trainings run inside.",
                    "Open one to watch its creation progress, then trigger and follow trainings with the architecture and sample rate you want.",
                ]} />
            </div>

            {createdNoticeVisible && <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded px-4 py-2 flex justify-between items-center">
                <span>New experiment created. It may take a few seconds to appear in the list.</span>
                {refreshCountdown > 0
                    ? <span className="font-semibold tabular-nums px-4">{refreshCountdown}</span>
                    : <ButtonWrap contents="Refresh" onClick={() => { fetchExperiments(); setCreatedNoticeVisible(false); }} />}
            </div>}
            <Table data={data} columns={columns} additionalButton={{ contents: "New Experiment", onClick: () => openModal() }} />
            {modalVisible && <Modal
                visible={modalVisible}
                closable={true}
                maskClosable={true}
                onClose={closeModal}>{<ExperimentNewModal closeModal={closeModal} onCreated={() => { setCreatedNoticeVisible(true); setRefreshCountdown(5); }} />}</Modal>}
        </div>
    )
}

export default ExperimentsPage